import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { Download } from "lucide-react";

export default function AdminSales() {
  const [items, setItems] = useState([]);
  const [process, setProcess] = useState(null);
  const [price, setPrice] = useState(0);
  const [notes, setNotes] = useState("");

  const load = async () => { const r = await api.get("/admin/sales"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const start = (s) => {
    setProcess(s);
    setPrice((s.purchase_price || 0) + (s.total_expenses || 0));
    setNotes("");
  };

  const submit = async () => {
    if (!price || price <= 0) return toast.error("Enter sale price");
    try {
      await api.post("/admin/sales/process", {
        sale_id: process.id, market_sale_price: +price, notes,
      });
      toast.success("Sale processed");
      setProcess(null); load();
    } catch (_) { toast.error("Failed"); }
  };

  const downloadInvoice = async (id, invNo) => {
    const token = localStorage.getItem("gf_token");
    const r = await fetch(`${API}/invoices/sale/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${invNo || "sale_invoice"}.pdf`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <h1 className="font-display text-3xl mb-6">Sales</h1>
      <div className="gf-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F4E9] text-xs uppercase tracking-wider text-[#6B5F58]">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Lot</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-right px-4 py-3">Investment</th>
              <th className="text-right px-4 py-3">Expenses</th>
              <th className="text-right px-4 py-3">Sale</th>
              <th className="text-right px-4 py-3">Net</th>
              <th className="text-left px-4 py-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-[#E8E0D2]">
                <td className="px-4 py-3 text-xs">{(s.requested_at || "").slice(0,10)}</td>
                <td className="px-4 py-3 text-sm">{s.lot?.lot_code}</td>
                <td className="px-4 py-3 text-sm">{s.user?.full_name}</td>
                <td className="px-4 py-3 text-right">₹{(s.purchase_price || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">₹{(s.total_expenses || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">{s.market_sale_price ? `₹${s.market_sale_price.toLocaleString()}` : "—"}</td>
                <td className="px-4 py-3 text-right">{s.net_amount_to_customer ? `₹${s.net_amount_to_customer.toLocaleString()}` : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    s.status === "completed" ? "bg-[#FCEDED] text-[#D71921]"
                    : "bg-amber-100 text-amber-800"}`}>{s.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  {s.status === "requested" && (
                    <button onClick={() => start(s)} data-testid={`process-sale-${s.id}`}
                      className="text-[#D71921] text-sm hover:underline">Process</button>
                  )}
                  {s.status === "completed" && (
                    <button onClick={() => downloadInvoice(s.id, s.invoice_number)} className="text-[#D71921] text-sm flex items-center gap-1 ml-auto">
                      <Download className="w-3 h-3" /> Invoice
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-[#6B5F58]">No sales yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {process && (
        <div className="fixed inset-0 z-50 backdrop-blur-md bg-stone-900/40 grid place-items-center p-4" onClick={() => setProcess(null)}>
          <div className="bg-white rounded-2xl border border-[#E8E0D2] max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl">Process sale</div>
            <div className="text-sm text-[#6B5F58] mb-4">{process.lot?.lot_code} · {process.user?.full_name}</div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-[#F8F4E9] border border-[#E8E0D2] text-sm">
                <div><div className="text-xs text-[#6B5F58]">Investment</div>₹{process.purchase_price?.toLocaleString()}</div>
                <div><div className="text-xs text-[#6B5F58]">Expenses</div>₹{process.total_expenses?.toLocaleString()}</div>
              </div>
              <label className="block">
                <span className="text-xs text-[#6B5F58]">Market sale price (₹)</span>
                <input data-testid="sale-price-input" type="number" value={price} onChange={(e) => setPrice(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-2.5 outline-none focus:border-[#D71921]" />
              </label>
              <label className="block">
                <span className="text-xs text-[#6B5F58]">Notes</span>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-2.5 outline-none focus:border-[#D71921]" />
              </label>
              <button onClick={submit} data-testid="confirm-sale-btn"
                className="w-full px-4 py-2.5 rounded-xl bg-[#D71921] text-white font-medium">Process & Generate Invoice</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
